import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OccasionalSlot, OccasionalSlotLimit } from '@/types/Slots'
import { Accessibility, AccessibilityType } from '@/types/Daycare'
import styles from './bookOccasionalSlot.module.scss'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { BOOK, BOOK_MERGER } from '@/modules/API/slot/bookingHandler'
import { useMutation } from '@apollo/client'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { ArrowForward, CalendarMonth, Check } from '@mui/icons-material'
import TextArea from '@/components/_custom/TextArea/textArea'
import moment from 'moment/moment'
import { Parent } from '@/types/Parent'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker'
import { getDaycareClosing, getDaycareOpening } from '@/modules/helper/daycareHelper'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { Child } from '@/types/Child'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'

interface BookOccasionalSlotProps {
    handleClose: () => void
    occasionalSlots: OccasionalSlot[]
    refetchData: () => void
    closeParentDialog: () => void
    parent?: Parent
}

const BOOK_MIN_MULTIPLIER: number = 30

const BookOccasionalSlot: FunctionComponent<BookOccasionalSlotProps> = ({
    handleClose,
    occasionalSlots,
    refetchData,
    closeParentDialog,
    parent
}: BookOccasionalSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const [startHour, setStartHour] = useState<string>(moment(occasionalSlots[0].start, 'HH:mm:ss').format('HH:mm'))
    const [endHour, setEndHour] = useState<string>(moment(occasionalSlots[0].end, 'HH:mm:ss').format('HH:mm'))

    const [duration, setDuration] = useState<number>(
        moment.duration(moment(endHour, 'HH:mm').diff(moment(startHour, 'HH:mm'))).asHours()
    )

    const [limit] = useState<OccasionalSlotLimit[] | null>(occasionalSlots[0].limit)

    const [limitReached, setLimitReached] = useState<boolean>(false)

    const [comment, setComment] = useState<string>('')

    const childIdsAvailable: Child[] =
        parent?.childs.filter((child: Child) => {
            return occasionalSlots.some((slot: OccasionalSlot) => {
                return slot.matching.some((match: number): boolean => match === child.id)
            })
        }) || []

    const [selectChilds, setSelectChilds] = useState<string[]>(
        childIdsAvailable.map((child: Child): string => child.id.toString()).slice(0, occasionalSlots.length) || []
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options:
                childIdsAvailable.map((child: Child): SelectOptionDeprecated => {
                    return {
                        value: child.id.toString(),
                        name: child.firstname
                    }
                }) || []
        }
    ]

    const [bookOccasionalSlot, { loading: loadingBook }] = useMutation(BOOK, {
        variables: {
            daycare_id: daycare.id,
            date: moment(occasionalSlots[0].date, 'DD-MM-YYYY').format('DD-MM-YYYY'),
            from: moment(startHour, 'HH:mm').format('HH:mm'),
            to: moment(endHour, 'HH:mm').format('HH:mm'),
            child_id: selectChilds.map((child) => Number(child)),
            comment: comment,
            place_id: occasionalSlots[0].placeId,
            external_icon: occasionalSlots[0].externalIcon ? occasionalSlots[0].externalIcon : undefined
        },
        onCompleted: () => {
            refetchData()
            closeParentDialog()

            showNotificationSuccess(t('bookSuccess'))
        },
        onError: () => {
            showNotificationError(t('bookError'))
        }
    })

    const [bookMergerOccasionalSlot, { loading: loadingBookMerger }] = useMutation(BOOK_MERGER, {
        variables: {
            daycare_id: daycare.id,
            date: moment(occasionalSlots[0].date, 'DD-MM-YYYY').format('DD-MM-YYYY'),
            from: moment(startHour, 'HH:mm').format('HH:mm'),
            to: moment(endHour, 'HH:mm').format('HH:mm'),
            child_id: selectChilds.map((child) => Number(child)),
            comment: comment,
            place_id: occasionalSlots[0].placeId,
            external_icon: occasionalSlots[0].externalIcon ? occasionalSlots[0].externalIcon : undefined
        },
        onCompleted: () => {
            refetchData()
            closeParentDialog()

            showNotificationSuccess(t('bookSuccess'))
        },
        onError: () => {
            showNotificationError(t('bookError'))
        }
    })

    useEffect(() => {
        if (limit && limit.length > 0) {
            const tempDuration: number = moment
                .duration(moment(endHour, 'HH:mm').diff(moment(startHour, 'HH:mm')))
                .asHours()

            setDuration(tempDuration)

            setLimitReached(false)

            selectChilds.map((child: string): void => {
                if (limit.some((l: OccasionalSlotLimit): boolean => l.childId === Number(child))) {
                    if (!daycare.merger) {
                        if (
                            (limit.find((l: OccasionalSlotLimit): boolean => l.childId === Number(child))?.limit ||
                                0) === 0
                        ) {
                            setLimitReached(true)
                        }
                    } else {
                        if (
                            (limit.find((l: OccasionalSlotLimit): boolean => l.childId === Number(child))?.limit || 0) <
                            tempDuration
                        ) {
                            setLimitReached(true)
                        }
                    }
                }
            })
        }
    }, [startHour, endHour])

    const minHour: number = occasionalSlots[0].start
        ? Number(moment(occasionalSlots[0].start, 'HH:mm:ss').format('HH'))
        : Number(
              moment(
                  getDaycareOpening(daycare, (moment(occasionalSlots[0].date, 'DD-MM-YYYY').day() + 6) % 7),
                  'HH:mm'
              ).format('HH')
          )
    const minMinute: number = occasionalSlots[0].start
        ? Number(moment(occasionalSlots[0].start, 'HH:mm:ss').format('mm'))
        : Number(
              moment(
                  getDaycareOpening(daycare, (moment(occasionalSlots[0].date, 'DD-MM-YYYY').day() + 6) % 7),
                  'HH:mm'
              ).format('mm')
          )
    const maxHour: number = occasionalSlots[0].end
        ? Number(moment(occasionalSlots[0].end, 'HH:mm:ss').format('HH'))
        : Number(
              moment(
                  getDaycareClosing(daycare, (moment(occasionalSlots[0].date, 'DD-MM-YYYY').day() + 6) % 7),
                  'HH:mm'
              ).format('HH')
          )
    const maxMinute: number = occasionalSlots[0].end
        ? Number(moment(occasionalSlots[0].end, 'HH:mm:ss').format('mm'))
        : Number(
              moment(
                  getDaycareClosing(daycare, (moment(occasionalSlots[0].date, 'DD-MM-YYYY').day() + 6) % 7),
                  'HH:mm'
              ).format('mm')
          )

    const range: number = daycare.merger ? Number(daycare.bookRange.split('_')[1] || '15') : 15

    const disabledButton: boolean = selectChilds.length > occasionalSlots.length || loadingBookMerger || loadingBook

    const entireSlotIsInAccessibility: boolean = daycare.merger
        ? slotIsInAccessibility(occasionalSlots[0].start, occasionalSlots[0].end, daycare.accessibility)
        : false

    const startIsNotInAccessibility: boolean = entireSlotIsInAccessibility
        ? checkIfHoursInAccessibility(startHour, daycare.accessibility, true)
        : false
    const endIsNotInAccessibility: boolean = entireSlotIsInAccessibility
        ? checkIfHoursInAccessibility(endHour, daycare.accessibility, false)
        : false

    return (
        <>
            <div className={styles.Container}>
                <CalendarMonth className={styles.Icon} />
                <div className={styles.TextContainer}>
                    <p className={styles.FirstText}>
                        {format(parse(occasionalSlots[0].date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                            locale: getLocalFromLanguage()
                        })}
                    </p>
                    {!daycare.merger && (
                        <p className={styles.SecondText}>
                            {t('from') +
                                ' ' +
                                moment(occasionalSlots[0].start, 'HH:mm:ss').format('HH:mm') +
                                ' ' +
                                t('to').toLowerCase() +
                                ' ' +
                                moment(occasionalSlots[0].end, 'HH:mm:ss').format('HH:mm')}
                        </p>
                    )}
                </div>
            </div>
            <div className={styles.BreakLine}></div>
            {limitReached && daycare.merger && (
                <>
                    <ErrorMessage error={t('youHaveReachedYourLimit')} className={styles.ErrorMessage} />
                    <ul>
                        {selectChilds.map((child: string, index: number) => {
                            if (limit && limit.some((l: OccasionalSlotLimit): boolean => l.childId === Number(child))) {
                                const childLimit: OccasionalSlotLimit | undefined = limit.find(
                                    (l: OccasionalSlotLimit): boolean => l.childId === Number(child)
                                )

                                if (childLimit && childLimit.limit < duration) {
                                    return (
                                        <li key={index} className={styles.ListLimit}>
                                            {
                                                parent?.childs.find((c: Child): boolean => c.id === Number(child))
                                                    ?.firstname
                                            }
                                            <ArrowForward className={styles.Icon} />
                                            {childLimit.limit}{' '}
                                            {childLimit.limit > 1 ? t('hoursRemainings') : t('hourRemaining')}
                                        </li>
                                    )
                                }
                            }
                        })}
                    </ul>
                    <div className={styles.BreakLine}></div>
                </>
            )}
            <div className={styles.InputContainer}>
                {daycare.merger && (
                    <div className={styles.Input}>
                        <TimePicker
                            label={t('startHour')}
                            error={startIsNotInAccessibility}
                            value={startHour}
                            setValue={setStartHour}
                            parent
                            className={styles.UserInput}
                            minHour={minHour}
                            minMinute={minMinute}
                            maxHour={maxHour}
                            maxMinute={maxMinute}
                            endTime={
                                endHour
                                    ? moment(endHour, 'HH:mm').format('HH:mm')
                                    : moment().hours(maxHour).minutes(maxMinute).seconds(0).format('HH:mm')
                            }
                            minuteToDisable={range}
                            accessibilities={daycare.accessibility || []}
                            morning={true}
                        />
                        <TimePicker
                            label={t('endHour')}
                            error={endIsNotInAccessibility}
                            value={endHour}
                            setValue={setEndHour}
                            parent
                            className={styles.UserInput}
                            minHour={minHour}
                            minMinute={minMinute}
                            maxHour={maxHour}
                            maxMinute={maxMinute}
                            startTime={
                                startHour
                                    ? moment(startHour, 'HH:mm')
                                          .add(daycare.bookMin * BOOK_MIN_MULTIPLIER, 'minutes')
                                          .format('HH:mm')
                                    : moment().hours(minHour).minutes(minMinute).seconds(0).format('HH:mm')
                            }
                            minuteToDisable={range}
                            accessibilities={daycare.accessibility || []}
                            morning={false}
                        />
                    </div>
                )}
                {occasionalSlots.length > 0 && (
                    <div className={styles.Input}>
                        <SelectMultiple
                            selectOptions={selectOptionsChild}
                            selectValues={selectChilds}
                            setSelectValues={setSelectChilds}
                            className={styles.UserInput}
                            isParent
                            label={t('myChilds')}
                        />
                    </div>
                )}
                <div className={styles.TextAreaInput}>
                    <p className={styles.TextAreaTitle}>{t('comment')}</p>
                    <TextArea
                        placeholder={t('comment')}
                        className={styles.TextArea}
                        parent
                        maxCount={100}
                        value={comment}
                        onChange={(e) => setComment(e)}
                    />
                </div>
            </div>
            {selectChilds.length > occasionalSlots.length && <ErrorMessage error={t('reservedSelectChildsError')} />}
            <DialogActions>
                <ButtonTextIcon type={'close'} onClick={handleClose} parent nowrap />
                <ButtonTextIcon
                    type={'custom'}
                    customText={t('book')}
                    customIcon={<Check />}
                    onClick={daycare.merger ? bookMergerOccasionalSlot : bookOccasionalSlot}
                    parent
                    disabled={
                        disabledButton ||
                        (daycare.merger && limitReached) ||
                        selectChilds.length <= 0 ||
                        (daycare.merger && (startIsNotInAccessibility || endIsNotInAccessibility))
                    }
                    nowrap
                />
            </DialogActions>
        </>
    )
}

export default BookOccasionalSlot

function checkIfHoursInAccessibility(
    hour: string,
    accessibilities: Accessibility[] | undefined,
    isStart: boolean
): boolean {
    if (!accessibilities) return false

    const slots: Accessibility[] = accessibilities.filter(
        (a: Accessibility) => a.type === (isStart ? AccessibilityType.arrival : AccessibilityType.departure)
    )

    if (slots.length === 0) return false

    const toMinutes: (h: string) => number = (h: string): number => {
        const [hh, mm] = h.split(':').map(Number)
        return hh * 60 + mm
    }

    const h: number = toMinutes(hour)

    for (const slot of slots) {
        const start: number = toMinutes(slot.start)
        const end: number = toMinutes(slot.end)
        if (h >= start && h <= end) return false
    }

    return true
}

function slotIsInAccessibility(start: string, end: string, accessibility: Accessibility[] | undefined): boolean {
    if (accessibility === undefined || accessibility === null || accessibility.length === 0) {
        return false
    }

    const arrivalAccessibility: Accessibility[] = accessibility
        .filter((accessibility: Accessibility): boolean => accessibility.type === AccessibilityType.arrival)
        .filter((accessibility: Accessibility): boolean =>
            moment(start, 'HH:mm:ss').isBetween(
                moment(accessibility.start, 'HH:mm:ss'),
                moment(accessibility.end, 'HH:mm:ss'),
                undefined,
                '[]'
            )
        )
    const departureAccessibility: Accessibility[] = accessibility
        .filter((accessibility: Accessibility): boolean => accessibility.type === AccessibilityType.departure)
        .filter((accessibility: Accessibility): boolean =>
            moment(end, 'HH:mm:ss').isBetween(
                moment(accessibility.start, 'HH:mm:ss'),
                moment(accessibility.end, 'HH:mm:ss'),
                undefined,
                '[]'
            )
        )

    return arrivalAccessibility.length > 0 && departureAccessibility.length > 0
}
